import {Avatar, Box, Chip, Drawer, Grid, IconButton, Stack, Tab, Tabs, Typography} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import moment from "moment";
import {useEffect, useState} from "react";
import {
  BALANCE_RECORD_TYPES,
  BALANCE_RECORD_TYPE_MAP,
  IMG_BASE_URL,
  PAYMENT_STATUS,
  PAY_METHOD_MAP,
  POINT_CATEGORY_MAP,
  POINT_RECORD_TYPE_MAP,
  ROLE_LEVEL_MAP,
  TIEJUN_LEVEL_MAP,
  USER_LEVEL_MAP,
  USER_STATUS_MAP
} from "src/config/constants";
import {asyncRequest} from "src/services/network";
import SectionHeader from "../section-header/SectionHeader";
import UserInfo from "./UserInfo";
import MerchantInfo from "./MerchantInfo";
import CellImage from "./CellImage";
import dayjs from "dayjs";
import ConvertUtil from "src/utils/ConvertUtil";
import Label from "../Label";
import {CloseOutlined} from "@mui/icons-material";
import Decimal from "decimal.js"

const storeOrderColumns = [
  {
    field: "orderSN",
    headerName: "订单编号",
    width: 200,
    // editable: true,
  },
  {
    field: "totalPrice",
    headerName: "总金额",
    type: "number",
    width: 90,
  },
  {
    field: "payPrice",
    headerName: "支付金额",
    type: "number",
    width: 90,
    sortable: false,
  },
  {
    field: "payMethod",
    headerName: "支付方式",
    // description: 'This column has a value getter and is not sortable.',
    // width: 160,
    valueGetter: (value, row) => PAY_METHOD_MAP[value],
  },
  {
    field: "productData",
    headerName: "商品信息",
    // description: 'This column has a value getter and is not sortable.',
    width: 190,
    renderCell: ({row}) => {
      return row.productData.map((p) => (
        <Stack direction="row" spacing={1}>
          <CellImage src={p.cover} style={{width: 60, height: 60, objectFit: "contain"}}/>
          <Stack>
            <Typography>{p.name}</Typography>
            <Typography>x{p.count}</Typography>
          </Stack>
        </Stack>
      ));
    },
  },
  {
    field: "status",
    headerName: "状态",
    // description: 'This column has a value getter and is not sortable.',
    // width: 160,
    renderCell: ({row}) => {
      return (
        <Label variant="ghost" color={ConvertUtil.getOrderStatusColor(row.status)}>
          {ConvertUtil.getOrderStatusText(row.status)}
        </Label>
      );
    },
  },
  {
    field: "createdAt",
    headerName: "创建时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
  {
    field: "payTime",
    headerName: "付款时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => (value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : null),
  },
];

const orderColumns = [
  // { field: 'id', headerName: 'ID', width: 90 },
  {
    field: "orderSN",
    headerName: "订单编号",
    width: 200,
    // editable: true,
  },
  {
    field: "totalPrice",
    headerName: "订单金额",
    align: "center",
    type: "number",
    width: 90,
    valueGetter: (value, row) => `￥${value}`,
  },
  {
    field: "rangli",
    headerName: "让利金额(比例)",
    align: "center",
    width: 120,
    valueGetter: (value, row) => `￥${row.rangli}(${new Decimal(value).div(row.totalPrice).mul(100).toFixed(0)}%)`,
  },
  {
    field: "detail",
    headerName: "商家应收",
    type: "number",
    align: "center",
    width: 90,
    valueGetter: (value, row) => `￥${new Decimal(row.totalPrice).sub(row.rangli).toNumber()}`,
  },
  {
    field: "sid",
    headerName: "支付信息(金额 | 消费券 | 积分)",
    type: "number",
    align: "center",
    width: 220,
    valueGetter: (value, row) => `￥${row.payPrice} | ${row.deductionXfqV2} | ${row.deductionPointsV2}`,
  },
  {
    field: "payMethod",
    headerName: "支付方式",
    // description: 'This column has a value getter and is not sortable.',
    align: "center",
    width: 140,
    valueGetter: (value, row) => (row.payPrice == 0 ? "消费券支付" : PAY_METHOD_MAP[value]),
  },
  {
    field: "merchant",
    headerName: "商户",
    // description: 'This column has a value getter and is not sortable.',
    width: 190,
    renderCell: ({row}) => {
      if (!row.merchant) return null;
      return <UserInfo uid={row.merchant.mid} avatar={row.merchant.logo} nickname={row.merchant.name}
                       enablePreview={false}/>;
    },
  },
  // {
  //   field: "rangli1",
  //   headerName: "让利",
  //   // description: 'This column has a value getter and is not sortable.',
  //   // width: 160,
  //   valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  // },
  // {
  //   field: "version",
  //   headerName: "版本",
  //   // description: 'This column has a value getter and is not sortable.',
  //   // width: 160,
  //   valueGetter: (value, row) => `${value}.0`,
  // },
  {
    field: "createdAt",
    headerName: "创建时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm"),
  },
];

const pointColumns = [
  {field: "id", headerName: "ID", width: 90},
  // {
  //   field: "orderSN",
  //   headerName: "订单编号",
  //   width: 200,
  // },
  {
    field: "category",
    headerName: "类别",
    width: 200,
    valueGetter: (value, row) => POINT_CATEGORY_MAP[value],
  },
  {
    field: "amount",
    headerName: "数额",
    // type: "number",
    // width: 150,
  },
  {
    field: "current",
    headerName: "当前剩余",
    // type: "number",
    // width: 110,
    sortable: false,
  },
  {
    field: "type",
    headerName: "类型",
    // description: 'This column has a value getter and is not sortable.',
    // width: 160,
    valueGetter: (value, row) => POINT_RECORD_TYPE_MAP[value],
  },
  {
    field: "detail",
    headerName: "详情",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    // valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  },
  {
    field: "relation",
    headerName: "关联订单",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    // valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  },
  {
    field: "createdAt",
    headerName: "时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];
const balanceDhqColumns = [
  {field: "id", headerName: "ID", width: 90},
  // {
  //   field: "orderSN",
  //   headerName: "订单编号",
  //   width: 200,
  // },
  {
    field: "amount",
    headerName: "数额",
    // type: "number",
    // width: 150,
  },
  {
    field: "current",
    headerName: "当前剩余",
    // type: "number",
    // width: 110,
    sortable: false,
  },
  {
    field: "type",
    headerName: "类型",
    // description: 'This column has a value getter and is not sortable.',
    // width: 160,
    valueGetter: (value, row) => BALANCE_RECORD_TYPE_MAP[value],
  },
  {
    field: "detail",
    headerName: "详情",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    // valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  },
  {
    field: "relation",
    headerName: "关联订单",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    // valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  },
  {
    field: "createdAt",
    headerName: "时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];
const balanceColumns = [
  {field: "id", headerName: "ID", width: 90},
  // {
  //   field: "orderSN",
  //   headerName: "订单编号",
  //   width: 200,
  // },
  {
    field: "amount",
    headerName: "数额",
    // type: "number",
    // width: 150,
  },
  {
    field: "current",
    headerName: "当前剩余",
    // type: "number",
    // width: 110,
    sortable: false,
  },
  {
    field: "type",
    headerName: "类型",
    // description: 'This column has a value getter and is not sortable.',
    // width: 160,
    valueGetter: (value, row) => BALANCE_RECORD_TYPE_MAP[value],
  },
  {
    field: "detail",
    headerName: "详情",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    // valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  },
  {
    field: "relation",
    headerName: "关联订单",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    // valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  },
  {
    field: "createdAt",
    headerName: "时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

const txqColumns = [
  {field: "id", headerName: "ID", width: 90},
  // {
  //   field: "orderSN",
  //   headerName: "订单编号",
  //   width: 200,
  // },
  {
    field: "amount",
    headerName: "数额",
    // type: "number",
    // width: 150,
  },
  {
    field: "current",
    headerName: "当前剩余",
    // type: "number",
    // width: 110,
    sortable: false,
  },
  {
    field: "detail",
    headerName: "详情",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    // valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  },
  {
    field: "relation",
    headerName: "关联订单",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    // valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  },
  {
    field: "createdAt",
    headerName: "时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

const parentColumns = [
  {
    field: "parent",
    headerName: "当前推荐人",
    // description: 'This column has a value getter and is not sortable.',
    width: 220,
    renderCell: ({row}) => {
      return <UserInfo {...(row.parent || {})} enablePreview={false}/>;
    },
  },
  {
    field: "prevParent",
    headerName: "之前推荐人",
    // description: 'This column has a value getter and is not sortable.',
    width: 220,
    renderCell: ({row}) => {
      return <UserInfo {...(row.prevParent || {})} enablePreview={false}/>;
    },
  },
  {
    field: "operator",
    headerName: "操作人",
    // description: 'This column has a value getter and is not sortable.',
    width: 120,
  },
  {
    field: "createdAt",
    headerName: "操作时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

const childListColumns = [
  {
    field: "uid",
    headerName: "被推荐人",
    width: 220,
    renderCell: ({row}) => {
      return <UserInfo {...(row || {})} enablePreview={false}/>;
    },
  },
  {field: "mobile", headerName: "手机号", width: 120},
  {field: "jytId", headerName: "金银通ID", width: 100},
  {field: "income", headerName: "产出金豆", width: 100},
  {field: "coin_recharge", headerName: "充值金豆", width: 100},
  {field: "equityValue", headerName: "权益值", width: 100},
  {field: "directValue", headerName: "直推业绩", width: 100},
  {
    field: "createdAt",
    headerName: "注册时间",
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

const parentListColumns = [
  {
    field: "idx",
    headerName: "层级",
    width: 100,
    renderCell: ({row, api}) => {
      // console.log(api.getRowIndexRelativeToVisibleRows(row.uid))
      return `第${api.getRowIndexRelativeToVisibleRows(row.uid) + 1}级`;
    },
  },
  {
    field: "uid",
    headerName: "推荐人",
    width: 220,
    renderCell: ({row}) => {
      return <UserInfo {...(row || {})} enablePreview={false}/>;
    },
  },
  {field: "mobile", headerName: "手机号", width: 120},
  // { field: "performance", headerName: "个人业绩", width: 100 },
  // { field: "teamPerformance", headerName: "团队业绩", width: 120 },
  {field: "childCount", headerName: "直推人数", width: 100},
  {field: "descendants", headerName: "团队人数", width: 120},
  {
    field: "createdAt",
    headerName: "注册时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

const promotedMerchantColumns = [
  {
    field: "mid",
    headerName: "ID",
    width: 120,
  },
  {
    field: "name",
    headerName: "名称",
    width: 290,
  },
  {
    field: "logo",
    headerName: "logo",
    // description: 'This column has a value getter and is not sortable.',
    width: 190,
    renderCell: ({row}) => {
      // if (!row.merchant) return null;
      return <CellImage src={row.logo}/>;
      // return <MerchantInfo {...row.merchant} enablePreview={false} />;
    },
  },
  {
    field: "pointsGained",
    headerName: "累计让利",
    width: 120,
  },
  {
    field: "createdAt",
    headerName: "入驻时间",
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

export default function UserInfoDrawer({nickname, avatar, mobile, uid, open, onClose}) {
  const [value, setValue] = useState("用户信息");

  const [totalCommission, setTotalCommission] = useState(0);
  const [info, setInfo] = useState({});
  const [orders, setOrders] = useState([]);
  const [storeOrders, setStoreOrders] = useState([]);
  const [pointRecordsV2, setPointRecordsV2] = useState([]);
  const [pointRecords, setPointRecords] = useState([]);
  const [xfqRecordsV2, setXfqRecordsV2] = useState([]);
  const [dhqRecordsV2, setDhqRecordsV2] = useState([]);
  const [xfqRecords, setXfqRecords] = useState([]);
  const [txqRecords, setTxqRecords] = useState([]);
  const [parentRecords, setParentRecords] = useState([]);
  const [childList, setChildList] = useState([]);
  const [merchants, setMerchants] = useState([]); // 名下的
  const [promotedMerchants, setPromotedMerchants] = useState([]);
  const [parentList, setParentList] = useState([]);

  const filter = {
    page: 0,
    size: 10,
  };

  const handleChange = (event, newValue) => {
    switch (newValue) {
      case "商城订单":
        getStoreOrders();
        break;
      case "线下订单":
        getOrders();
        break;
      case "兑换券":
        getdhqV2List(2);
        break;
      case "2.0积分明细":
        getPointRecords(2);
        break;
      case "1.0积分明细":
        getPointRecords(1);
        break;
      case "2.0消费券明细":
        getXfqRecords(2);
        break;
      case "1.0消费券明细":
        getXfqRecords(1);
        break;
      case "1.0消费额度明细":
        getXfqRecords(1);
        break;
      case "添星券明细":
        getTxqRecords();
        break;
      case "推荐人变更记录":
        getParentRecords();
        break;
      case "名下商家":
        getMyMerchants();
        break;
      case "推广商家":
        getPromotedMerchants();
        break;
      case "推广粉丝":
        getChildList();
        break;
      case "推荐人关系":
        getParentList();
        break;
    }
    setValue(newValue);
  };

  useEffect(() => {
    if (!uid || !open) return;
    getUserInfo();
    // getTotalCommission();
  }, [uid, open]);

  async function getTotalCommission() {
    const {data} = await asyncRequest({
      method: "get",
      path: `/user/commission/${uid}`,
    });

    setTotalCommission(data);
  }

  async function getUserInfo() {
    const {data} = await asyncRequest({
      method: "get",
      path: `/user/detail/${uid}`,
      // data: {
      //   uid,
      //   status: PAYMENT_STATUS.PAYED,
      // },
    });

    setInfo(data);
  }

  async function getOrders() {
    const {data} = await asyncRequest({
      path: "/payment/list",
      data: {
        uid,
        status: PAYMENT_STATUS.PAYED,
        page: 0,
        size: 2000,
      },
    });

    setOrders(data);
  }

  async function getStoreOrders() {
    const {data} = await asyncRequest({
      path: "/store/order/list",
      data: {
        uid,
        page: 0,
        size: 1000,
        status: -1,
      },
    });

    setStoreOrders(
      data.map((d) => ({
        ...d,
        createdAt: dayjs(d.createdAt).format("YYYY-MM-DD HH:mm:ss"),
        payTime: d.payTime ? dayjs(d.payTime).format("YYYY-MM-DD HH:mm:ss") : d.payTime,
        productData: JSON.parse(d.productData) || [],
      }))
    );
  }

  async function getdhqV2List(version) {
    const {data} = await asyncRequest({
      path: "/finance/dhqV2/list",
      data: {
        uid,
        page: 0,
        size: 2000,
        version,
      },
    });

    setDhqRecordsV2(data);
  }

  async function getPointRecords(version) {
    const {data} = await asyncRequest({
      path: "/point/record/list",
      data: {
        uid,
        page: 0,
        size: 2000,
        version,
      },
    });

    if (version == 2) {
      setPointRecordsV2(data);
      return;
    }

    setPointRecords(data);
  }

  async function getXfqRecords(version) {
    const {data} = await asyncRequest({
      path: "/finance/xfq/list",
      data: {
        uid,
        page: 0,
        size: 2000,
        version,
      },
    });

    if (version == 2) {
      setXfqRecordsV2(data);
      return;
    }

    setXfqRecords(data);
  }

  async function getTxqRecords() {
    const {data} = await asyncRequest({
      path: "/finance/txq/list",
      data: {
        uid,
        page: 0,
        size: 2000,
      },
    });

    setTxqRecords(data);
  }

  async function getParentRecords() {
    const {data} = await asyncRequest({
      path: "/user/parent/record/list",
      data: {
        uid,
        page: 0,
        size: 2000,
      },
    });

    setParentRecords(data);
  }

  async function getMyMerchants() {
    const {data} = await asyncRequest({
      path: "/merchant/getByAdmin",
      data: {
        uid,
        page: 0,
        size: 2000,
      },
    });

    setMerchants(data);
  }

  async function getChildList() {
    const {data} = await asyncRequest({
      path: "/user/childList",
      data: {
        uid,
        page: 0,
        size: 2000,
      },
    });

    setChildList(data);
  }

  async function getParentList() {
    const {data} = await asyncRequest({
      path: "/user/parentHierarchy",
      data: {
        uid,
        page: 0,
        size: 2000,
      },
    });

    setParentList(data);
  }

  async function getPromotedMerchants() {
    const {data} = await asyncRequest({
      path: "/merchant/list",
      data: {
        uid,
        pid: info.uid,
        page: 0,
        size: 2000,
      },
    });

    setPromotedMerchants(data);
  }

  return (
    <>
      {open && (
        <Drawer anchor="right" open onClose={onClose}>
          <Box sx={{width: "90vw"}} p={2}>
            <Stack alignContent="space-between" direction="row">
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  // cursor: "pointer",
                  width: "100%",
                }}
                alignSelf={1}
              >
                <Avatar alt={nickname} src={`${IMG_BASE_URL}${avatar}?w=60`}/>
                <Stack>
                  <Typography variant="subtitle2" noWrap>
                    {nickname || mobile}
                  </Typography>
                  <Typography variant="subtitle2" noWrap>
                    {uid}
                  </Typography>
                </Stack>
                {info.tiejun ? <Chip label={[TIEJUN_LEVEL_MAP[info.tiejun.level]]} color="primary"/> : null}
              </Stack>

              <IconButton onClick={onClose}>
                <CloseOutlined/>
              </IconButton>
            </Stack>

            <Grid container mt={3}>
              <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>2.0消费券</Typography>
                  <Typography>{info.xfqV2 || 0}</Typography>
                </Stack>
              </Grid>
              <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>1.0消费券</Typography>
                  <Typography>
                    {info.xfq || 0}(可用{info.xfqFree})
                  </Typography>
                </Stack>
              </Grid>

              <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>2.0绿积分</Typography>
                  <Typography>{info.pointsV2 || 0}</Typography>
                </Stack>
              </Grid>

              <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>1.0绿积分</Typography>
                  <Typography>{info.pointsGreen || 0}</Typography>
                </Stack>
              </Grid>

              <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>2.0兑换券</Typography>
                  <Typography>{info.dhqV2 || 0}</Typography>
                </Stack>
              </Grid>

              <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>1.0兑换券</Typography>
                  <Typography>{info.dhq || 0}</Typography>
                </Stack>
              </Grid>

              <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>添星券</Typography>
                  <Typography>{info.txq || 0}</Typography>
                </Stack>
              </Grid>
              {/*
              <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>公益积分</Typography>
                  <Typography>{info.pointsCharity || 0}</Typography>
                </Stack>
              </Grid> */}
              {/* <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>总订单数</Typography>
                  <Typography>{info.payedOrders || 0}</Typography>
                </Stack>
              </Grid> */}
              <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>总消费额</Typography>
                  <Typography>{info.consumption || 0}</Typography>
                </Stack>
              </Grid>
              <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>累计获得消费券</Typography>
                  <Typography>{info.totalReleased || 0}</Typography>
                </Stack>
              </Grid>
            </Grid>

            <Box sx={{width: "100%"}}>
              <Box sx={{borderBottom: 1, borderColor: "divider"}}>
                <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                  <Tab label="用户信息" value="用户信息"/>
                  <Tab label="商城订单" value="商城订单"/>
                  <Tab label="线下订单" value="线下订单"/>
                  <Tab label="2.0积分明细" value="2.0积分明细"/>
                  <Tab label="2.0消费券明细" value="2.0消费券明细"/>
                  <Tab label="2.0兑换券" value="兑换券"/>
                  <Tab label="1.0积分明细" value="1.0积分明细"/>
                  <Tab label="1.0消费券明细" value="1.0消费券明细"/>
                  <Tab label="添星券明细" value="添星券明细"/>
                  <Tab label="推荐人变更记录" value="推荐人变更记录"/>
                  <Tab label="推广粉丝" value="推广粉丝"/>
                  <Tab label="推荐人关系" value="推荐人关系"/>
                  {!!info.miniAdmins && <Tab label="名下商家" value="名下商家"/>}
                  {!!info.tiejun && <Tab label="推广商家" value="推广商家"/>}
                </Tabs>
              </Box>

              <Box sx={{height: 700, width: "100%"}}>
                {value == "用户信息" && (
                  <Stack p={2}>
                    <SectionHeader title="基础信息"/>
                    <Grid container py={1} rowGap={1}>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>用户ID：</Typography>
                          <Typography>{info.uid}</Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>用户昵称：</Typography>
                          <Typography>{info.nickname}</Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>手机号码：</Typography>
                          <Typography>{info.mobile}</Typography>
                        </Stack>
                      </Grid>
                    </Grid>

                    <SectionHeader title="用户概况" mt={2}/>
                    <Grid container py={1} rowGap={1}>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>用户状态：</Typography>
                          <Typography>{USER_STATUS_MAP[info.status]}</Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>用户等级：</Typography>
                          <Typography>{USER_LEVEL_MAP[info.level]}</Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>联创：</Typography>
                          <Typography>
                            {info.isHehuo ? "合伙人" : ""} {info.isDaiyan ? "代言人" : ""}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>渠道等级：</Typography>
                          <Typography>{ROLE_LEVEL_MAP[info.roleLevel]}</Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>注册时间：</Typography>
                          <Typography>{moment(info.createdAt).format("YYYY-MM-DD HH:mm:ss")}</Typography>
                        </Stack>
                      </Grid>
                    </Grid>

                    <SectionHeader title="推荐关系" mt={2}/>
                    <Grid container py={1} rowGap={1}>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>推荐人：</Typography>
                          {info.fromMer ? <MerchantInfo {...info.parent} enablePreview={false}/> :
                            <UserInfo {...info.parent} enablePreview={false}/>}
                        </Stack>
                      </Grid>
                    </Grid>
                  </Stack>
                )}
                {value === "线下订单" && (
                  <DataGrid
                    rows={orders}
                    columns={orderColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 2.0兑换券 */}
                {value == "兑换券" && (
                  <DataGrid
                    rows={dhqRecordsV2}
                    columns={balanceDhqColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {value == "商城订单" && (
                  <DataGrid
                    rows={storeOrders}
                    columns={storeOrderColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 积分明细 */}
                {value == "2.0积分明细" && (
                  <DataGrid
                    rows={pointRecordsV2}
                    columns={pointColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 积分明细 */}
                {value == "1.0积分明细" && (
                  <DataGrid
                    rows={pointRecords}
                    columns={pointColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 余额明细 */}
                {value == "2.0消费券明细" && (
                  <DataGrid
                    rows={xfqRecordsV2}
                    columns={balanceColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 余额明细 */}
                {value == "1.0消费券明细" && (
                  <DataGrid
                    rows={xfqRecords}
                    columns={balanceColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 添星券明细 */}
                {value == "添星券明细" && (
                  <DataGrid
                    rows={txqRecords}
                    columns={txqColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 推荐人关系 */}
                {value == "推荐人关系" && (
                  <DataGrid
                    rows={parentList}
                    columns={parentListColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    getRowId={(row) => row.uid}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 推荐人 */}
                {value == "推荐人变更记录" && (
                  <DataGrid
                    rows={parentRecords}
                    columns={parentColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 推广粉丝 */}
                {value == "推广粉丝" && (
                  <DataGrid
                    rows={childList}
                    columns={childListColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    getRowId={(row) => row.uid}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 名下商家 */}
                {value == "名下商家" && (
                  <DataGrid
                    getRowId={(r) => r.mid}
                    rows={merchants}
                    columns={promotedMerchantColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 推广商家 */}
                {value == "推广商家" && (
                  <DataGrid
                    getRowId={(r) => r.mid}
                    rows={promotedMerchants}
                    columns={promotedMerchantColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Drawer>
      )}
    </>
  );
}
